import { useState } from "react";

import { formatDisplayDateTime } from "../../utils/helperFunctions";
import { deliveryLabel } from "../../utils/deliveryLabel";

/**
 * Delivery QC rounds for an ad request, newest first.
 *
 * Shared by the requester side (AdRequestDetailPage) and the agency side
 * (AdDeliverDetailModal) so both read the same wording for the same feedback.
 *
 * Amber is reserved for the ONE round that is still open — history in amber
 * reads as unresolved and drowns out what actually needs attention.
 */

const QcRound = ({ entry, totalRounds }) => {
  const isChanges = entry.action === "request_changes";
  const reuploaded = isChanges && !!entry.resolved_at;
  const open = isChanges && !entry.resolved_at;
  const hooks = entry.affected_hooks ?? [];
  const backCount = entry.resolved_hooks?.length ?? 0;
  const partial = open && backCount > 0;

  // Amber: the agency still owes us something. Emerald: they delivered and it is
  // the reviewer's turn — that is the signal worth seeing first, so it drives the
  // rule and gets a badge rather than a grey footnote.
  const accent = reuploaded
    ? "border-emerald-400 bg-emerald-50/40"
    : open
      ? "border-amber-400 bg-amber-50/40"
      : "border-gray-200";

  return (
    <div className={`border-l-[3px] pl-3 py-1.5 rounded-r ${accent}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={`text-xs font-semibold ${
            open
              ? "text-amber-700"
              : isChanges
                ? "text-gray-600"
                : "text-emerald-700"
          }`}
        >
          {isChanges
            ? totalRounds > 1
              ? `Round ${entry.round} · Changes requested`
              : "Changes requested"
            : "Approved → moderation"}
        </span>

        {/* Status as a badge in the header, not a line at the bottom: "they
            re-uploaded" is the reason a reviewer opens this at all. */}
        {reuploaded && (
          <span className="inline-flex items-center rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-semibold text-white">
            Re-uploaded · review again
          </span>
        )}
        {partial && (
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
            {backCount} of {hooks.length} re-uploaded
          </span>
        )}
        {open && !partial && (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
            Awaiting re-upload
          </span>
        )}

        <span className="text-xs text-gray-400">
          {entry.performed_by_name ? `${entry.performed_by_name} · ` : ""}
          {formatDisplayDateTime(entry.created_on)}
        </span>
      </div>

      {/* One note per delivery — a round has no shared comment of its own.
          Each delivery is its own card: a compact id badge + the (long) ad name
          on top, then the feedback on its own full-width line below — so the
          name never crowds the note. */}
      {isChanges && hooks.length > 0 && (
        <div className="mt-2 space-y-2">
          {hooks.map((hook) => {
            const name = (hook.hook_name || "").trim();
            return (
              <div
                key={hook.id ?? deliveryLabel(hook)}
                className="rounded-md border border-gray-100 bg-gray-50/70 px-2.5 py-2"
              >
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span
                    className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold shrink-0 ${
                      open
                        ? "bg-amber-100 text-amber-700"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {hook.id ? `Delivery #${hook.id}` : name || "Delivery"}
                  </span>
                  {hook.id && name && (
                    <span className="min-w-0 break-all font-mono text-[11px] text-gray-400">
                      {name}
                    </span>
                  )}
                </div>
                {hook.feedback && (
                  <p className="mt-1.5 whitespace-pre-wrap tracking-wide text-[12px] text-gray-800">
                    {hook.feedback}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* The badge above already says what state this is; this line only adds
          when and by whom, so it stays quiet. */}
      {reuploaded && (
        <p className="mt-1 text-xs font-medium text-emerald-700">
          {entry.resolved_by_name ? `${entry.resolved_by_name} · ` : ""}
          {formatDisplayDateTime(entry.resolved_at)}
        </p>
      )}
    </div>
  );
};

const QcHistory = ({ history }) => {
  const [expanded, setExpanded] = useState(false);

  if (!history?.length) return null;

  // Number rounds by their real order, then flip for reading: what is open
  // matters more than what happened first.
  const rounds = history
    .map((entry, i) => ({ ...entry, round: i + 1 }))
    .reverse();
  const changeRounds = rounds.filter(
    (r) => r.action === "request_changes"
  ).length;
  const visible = expanded ? rounds : rounds.slice(0, 1);
  const hidden = rounds.length - visible.length;

  return (
    <div className="space-y-3">
      {visible.map((entry) => (
        <QcRound key={entry.id} entry={entry} totalRounds={changeRounds} />
      ))}

      {(hidden > 0 || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs font-medium text-primaryPurple hover:underline"
        >
          {hidden > 0
            ? `Show ${hidden} earlier ${hidden === 1 ? "entry" : "entries"}`
            : "Show less"}
        </button>
      )}
    </div>
  );
};

export default QcHistory;
